/**
 * A dependency injection container which closely resembles the one in the
 * Laravel PHP framework. A lot of code is ported directly from Laravel.
 *
 * @class Container
 */
class Container {

  constructor() {
    this.bindings  = new Map();
    this.instances = new Map();
  }

  /**
   * Register a binding in the container.
   *
   * @param  {String|Class}   abstract
   * @param  {Class|Function} concrete
   * @param  {Boolean}        shared
   * @return {Void}
   */
  bind(abstract, concrete = null, shared = false) {
    if ( ! concrete) {
      concrete = abstract;
    }

    // Check if concrete actually is a class instead of a regular
    // function. If it is, wrap it in a closure that returns the
    // actual object, so that all bindings behave the same.
    if (this.isClass(concrete)) {
      concrete = this.getClosure(concrete);
    }

    this.bindings.set(abstract, { concrete, shared });
  }

  /**
   * Register a singleton in the container.
   *
   * @param  {String|Class}   abstract
   * @param  {Class|Function} concrete
   * @return {Void}
   */
  singleton(abstract, concrete = null) {
    this.bind(abstract, concrete, true);
  }

  /**
   * Register an instance in the container.
   *
   * @param  {String|Class} abstract
   * @param  {Object}       instance
   * @return {Void}
   */
  instance(abstract, instance) {
    this.instances.set(abstract, instance);
  }

  /**
   * Resolve an instance from the container.
   *
   * @param  {String|Class} abstract
   * @return {Object}
   */
  make(abstract) {
    if (this.instances.has(abstract)) {
      return this.instances.get(abstract);
    }

    let object = this.build(abstract);

    if (this.isShared(abstract)) {
      this.instances.set(abstract, object);
    }

    return object;
  }

  /**
   * Get an object either from a registered binding or
   * by automatically building it.
   *
   * @param  {String|Class} abstract
   * @return {Object}
   */
  build(abstract) {
    if (this.bindings.has(abstract)) {
      return this.bindings.get(abstract).concrete();
    }

    if (typeof abstract !== 'function') {
      throw new Error(`No binding found for ${abstract}`);
    }

    return this.buildObject(abstract);
  }

  /**
   * Build an object from a class and pass it its
   * resolved dependencies in the constructor.
   *
   * @param  {Class|Function} concrete
   * @return {Object}
   */
  buildObject(concrete) {
    let dependencies = concrete.depends;

    if ( ! dependencies) {
      return new concrete();
    }

    let instances = this.getDependencies(dependencies);

    return new concrete(...instances);
  }

  /**
   * Wrap an instantiation (with dependencies) of concrete in a closure.
   *
   * @param  {Class|Function} concrete
   * @return {Function}
   */
  getClosure(concrete) {
    return () => {
      return this.buildObject(concrete);
    };
  }

  /**
   * Map over an array of dependencies and recursively
   * resolve them out of the container.
   *
   * @param  {Array} parameters
   * @return {Array}
   */
  getDependencies(parameters) {
    return parameters.map(param => this.make(param));
  }

  /**
   * Check if the given function is a class constructor.
   *
   * @param  {Class|Function} concrete
   * @return {Boolean}
   */
  isClass(concrete) {
    // Regular constructor calls without the 'new' operator returns
    // 'undefined', and ES2015 classes throw a TypeError.
    try {
      if (typeof concrete() === 'undefined') {
        return true;
      }
    } catch (e) {
      if (e instanceof TypeError) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if the given binding is shared.
   *
   * @param  {String|Class}  abstract
   * @return {Boolean}
   */
  isShared(abstract) {
    return this.bindings.has(abstract) && this.bindings.get(abstract).shared === true;
  }

}

/**
 * The singleton instance of the Container.
 *
 * @type {Container}
 */
let app = new Container();

/**
 * ES7 decorator function which allows classes to declare the bindings
 * or objects that should be passed to its constructor. The class
 * must be instantiated via the container for this to work.
 *
 * @param  {...String|Class} dependencies
 * @return {Class}
 */
export function depends(...dependencies) {
  return (target) => {
    target.depends = dependencies;
  }
}

/**
 * ES7 decorator which injects dependencies in a slightly more hacky way -
 * direcly as properties on the prototype. This doesn't require that
 * the class is instantiated via the container. Use this if you
 * can't control the instantiation of the class yourself.
 *
 * @param  {Object} dependencies
 * @return {Class}
 */
export function inject(dependencies) {
  return (target) => {
    for (let key in dependencies) {
      target.prototype[key] = app.make(dependencies[key]);
    }
  }
}

export default app;



// WEBPACK FOOTER //
// ./src/utils/Container.js